<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const { content, attachments, contentAttributes, messageType } =
  useMessageContext();
const { t } = useI18n();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const eventData = computed(() => contentAttributes.value?.data || {});

const interactionEventLabel = computed(() => {
  if (eventData.value.event_type === 'facebook_postback') {
    return t('CONVERSATION.MESSAGE_EVENTS.MESSENGER_POSTBACK');
  }

  if (eventData.value.event_type === 'facebook_quick_reply') {
    return t('CONVERSATION.MESSAGE_EVENTS.MESSENGER_QUICK_REPLY');
  }

  if (eventData.value.event_type === 'widget_postback') {
    return t('CONVERSATION.MESSAGE_EVENTS.WEB_WIDGET_POSTBACK');
  }

  return '';
});

const parsedInteractionPayload = computed(() => {
  if (eventData.value.product_name || eventData.value.product_action) {
    return {
      action: eventData.value.product_action,
      name: eventData.value.product_name,
      price: eventData.value.price,
      currency: eventData.value.currency,
      sku_id: eventData.value.sku_id,
      ref_id: eventData.value.ref_id,
    };
  }

  try {
    const parsed = JSON.parse(eventData.value.payload || '{}');
    return parsed?.type === 'product_action' ? parsed : null;
  } catch {
    return null;
  }
});

const formattedInteractionPrice = computed(() => {
  const amount = parsedInteractionPayload.value?.price?.toString().trim();
  if (!amount) {
    return '';
  }

  const currency = parsedInteractionPayload.value?.currency
    ?.toString()
    .trim()
    .toUpperCase();
  if (currency === 'USD') {
    return `$${amount}`;
  }

  if (!currency) {
    return amount;
  }

  return `${currency} ${amount}`;
});

const interactionPayload = computed(() => {
  if (!parsedInteractionPayload.value) {
    return eventData.value.payload || '';
  }

  const title = eventData.value.title || '';
  const productName = parsedInteractionPayload.value.name || '';
  const summary = [title, productName].filter(Boolean).join(' · ');

  if (!formattedInteractionPrice.value) {
    return summary || eventData.value.payload || '';
  }

  return `${summary} (${formattedInteractionPrice.value})`;
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="text">
    <div class="gap-3 flex flex-col">
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <div
        v-if="interactionEventLabel"
        class="flex flex-wrap items-center gap-2 text-xs text-n-slate-11"
      >
        <span
          class="inline-flex items-center rounded-full bg-n-alpha-2 px-2.5 py-1"
        >
          {{ interactionEventLabel }}
        </span>
        <span
          v-if="interactionPayload"
          class="inline-flex items-center rounded-full bg-n-alpha-2 px-2.5 py-1"
          :title="`${t('CONVERSATION.MESSAGE_EVENTS.PAYLOAD')}: ${interactionPayload}`"
        >
          {{ interactionPayload }}
        </span>
      </div>
      <FormattedContent v-if="renderContent" :content="renderContent" />
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
